import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconPencil, IconPlus, IconStar, IconTrash } from '@tabler/icons-react';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { readTemplateVariables } from '@/components/template-variable-editor';
import { getErrorMessage } from '@/lib/error-message';
import { isTemplatesEnabled } from '@/lib/features';
import { DeletePersonaTemplateMutation, PersonaTemplatesListSubscription } from './-components/graphql';

export const Route = createFileRoute('/_authed/system/persona-templates/')({
  beforeLoad: () => {
    if (!isTemplatesEnabled()) throw redirect({ to: '/' });
  },
  component: SystemPersonaTemplatesPage,
});

function SystemPersonaTemplatesPage() {
  const [{ data, error: subscriptionError }] = useSubscription({ query: PersonaTemplatesListSubscription });
  const [, runDeleteTemplate] = useMutation(DeletePersonaTemplateMutation);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!deletingId) return;

    setIsSubmitting(true);
    setError(null);

    const result = await runDeleteTemplate({ templateId: deletingId });

    setIsSubmitting(false);

    if (result.error) {
      setError(getErrorMessage(result.error, 'Failed to delete template'));

      return;
    }

    setDeletingId(null);
  };

  if (data === undefined && !subscriptionError) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const templates = data?.personaTemplates ?? [];

  return (
    <>
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Group justify="space-between">
            <div>
              <Title order={1}>Persona Templates</Title>
              <Text c="dimmed" mt="xs">
                Create reusable persona templates with configurable variables
              </Text>
            </div>
            <Button component={Link} to="/system/persona-templates/new" leftSection={<IconPlus size={16} />}>
              New Template
            </Button>
          </Group>

          <ErrorAlert
            message={subscriptionError ? getErrorMessage(subscriptionError, 'Failed to load templates') : null}
          />

          <Paper withBorder radius="lg">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Variables</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th w={120}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {templates.map((template) => (
                  <PersonaTemplateRow
                    key={template.id}
                    id={template.id}
                    name={template.name}
                    category={template.category ?? null}
                    featured={template.featured}
                    sortOrder={template.sortOrder}
                    variableCount={readTemplateVariables(template.variables).length}
                    onDelete={() => setDeletingId(template.id)}
                  />
                ))}
                {templates.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" ta="center" py="md">
                        No persona templates yet. Create your first template to get started.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      </Container>

      <Modal opened={!!deletingId} onClose={() => setDeletingId(null)} title="Delete Persona Template">
        <Stack>
          <ErrorAlert message={error} />
          <Text>Are you sure you want to delete this template? This action cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeletingId(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={isSubmitting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

type PersonaTemplateRowProps = {
  id: string;
  name: string;
  category: string | null;
  featured: boolean;
  sortOrder: number;
  variableCount: number;
  onDelete: () => void;
};

function PersonaTemplateRow({
  id,
  name,
  category,
  featured,
  sortOrder,
  variableCount,
  onDelete,
}: PersonaTemplateRowProps) {
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs">
          {name}
          {featured && (
            <Badge size="xs" color="yellow" leftSection={<IconStar size={10} />}>
              Featured
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {category || '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge size="sm" variant="light">
          {variableCount} var{variableCount !== 1 ? 's' : ''}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          Order: {sortOrder}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Tooltip label="Edit">
            <ActionIcon variant="subtle" component={Link} to={`/system/persona-templates/${id}`}>
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete">
            <ActionIcon variant="subtle" color="red" onClick={onDelete}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
